import { toWSDateString } from "@wingspanhq/fe-component-library";
import {
  IPayableSchema,
  IPayeeResponse
} from "@wingspanhq/payments/dist/interfaces";
import { IAuthorizationListResponse, INewUser } from "@wingspanhq/users";
import React, { useMemo } from "react";
import { useQueryAuthorizations } from "../../../query/users/queries";
import { useQueryPaymentsStatus } from "../../../query/payments/queries";
import { addBusinessDays } from "../../../utils/dates";
import { Activity, ActivityItem } from "../Activity";

type Props = { payable: IPayableSchema; payee: IPayeeResponse; user: INewUser };

export const PayableActivity: React.FC<Props> = props => {
  const { payable, payee, user } = props;
  const queryAuthorization = useQueryAuthorizations();
  const queryPaymentsStatus = useQueryPaymentsStatus();
  const payoutDelay = queryPaymentsStatus.data?.payoutDelay ?? 0;
  const authorizations = queryAuthorization.data || [];

  const approverEmail = getEventActorEmail(
    payable.eventActors?.approvedBy,
    user,
    authorizations
  );
  const preApproverEmail = getEventActorEmail(
    payable.eventActors?.preApprovedBy,
    user,
    authorizations
  );
  const unapproverEmail = getEventActorEmail(
    payable.eventActors?.unapprovedBy,
    user,
    authorizations
  );
  const payeeEmail = payee.user?.email;

  const activity = useMemo(() => {
    const activity: ActivityItem[] = [];

    activity.push({
      title: "Payable was created",
      description: toWSDateString(payable.createdAt),
      date: payable.createdAt
    });

    if (payable.events.openedAt) {
      activity.push({
        title: `Sent to ${user.email}`,
        description: toWSDateString(payable.events.openedAt),
        date: payable.events.openedAt
      });
    }

    if (
      payable.events.emailReceivedAt &&
      payable.events.emailReceivedAt.length > 0
    ) {
      activity.push({
        title: `Received by ${user.email}`,
        description: toWSDateString(payable.events.emailReceivedAt[0]),
        date: payable.events.emailReceivedAt[0]
      });
    }

    // Work with viewed at only if it is before paidAt and depositedAt
    // Viewed event should never come after paid and deposited
    const allowedViewedAt = (payable.events.emailViewedAt || []).filter(date =>
      payable.events.paidAt
        ? payable.events.paidAt > date
        : true && payable.events.depositedAt
          ? payable.events.depositedAt > date
          : true
    );

    if (allowedViewedAt.length > 0) {
      allowedViewedAt.sort((a, b) => b.getTime() - a.getTime());

      const firstViewedAt = allowedViewedAt[allowedViewedAt.length - 1];

      if (firstViewedAt) {
        activity.push({
          title: `Viewed by ${user.email}`,
          description: toWSDateString(firstViewedAt),
          date: firstViewedAt
        });
      }

      const secondViewedAt = firstViewedAt
        ? allowedViewedAt.find(
            date => date.getTime() - firstViewedAt.getTime() > 60 * 60 * 1000
          )
        : undefined;

      if (secondViewedAt) {
        activity.push({
          title: `Viewed by ${user.email}`,
          description: toWSDateString(secondViewedAt),
          date: secondViewedAt
        });
      }
    }

    if (payable.events.approvedAt) {
      activity.push({
        title: `Approved by ${approverEmail}`,
        description: toWSDateString(payable.events.approvedAt),
        date: payable.events.approvedAt
      });
    }

    if (payable.events.unapprovedAt) {
      activity.push({
        title: `Unapproved by ${unapproverEmail}`,
        description: toWSDateString(payable.events.unapprovedAt),
        date: payable.events.unapprovedAt
      });
    }

    if (payable.events.preApprovedAt) {
      activity.push({
        title: `Pre-approved by ${preApproverEmail}`,
        description: toWSDateString(payable.events.preApprovedAt),
        date: payable.events.preApprovedAt
      });
    }

    if (payable.events.sentDueIn3DaysAt) {
      activity.push({
        title: `Reminder sent to ${user.email}`,
        description: toWSDateString(payable.events.sentDueIn3DaysAt),
        date: payable.events.sentDueIn3DaysAt
      });
    }

    if (payable.events.sentDueTodayAt) {
      activity.push({
        title: `Reminder sent to ${user.email}`,
        description: toWSDateString(payable.events.sentDueTodayAt),
        date: payable.events.sentDueTodayAt
      });
    }

    if (payable.events.sentDue3DaysAgoAt) {
      activity.push({
        title: `Reminder sent to ${user.email}`,
        description: toWSDateString(payable.events.sentDue3DaysAgoAt),
        date: payable.events.sentDue3DaysAgoAt
      });
    }

    if (payable.events.sentDue7DaysAgoAt) {
      activity.push({
        title: `Reminder sent to ${user.email}`,
        description: toWSDateString(payable.events.sentDue7DaysAgoAt),
        date: payable.events.sentDue7DaysAgoAt
      });
    }

    if (payable.events.paymentInTransitAt) {
      activity.push({
        title: `Payment in transit on ${toWSDateString(
          payable.events.paymentInTransitAt
        )}`,
        description: "Waiting on wire transfer to arrive",
        date: payable.events.paymentInTransitAt
      });
    }

    if (payable.events.paidAt) {
      activity.push({
        title: `Paid on ${toWSDateString(payable.events.paidAt)}`,
        description: payable.events.estimatedDepositAt
          ? `Expected deposit on ${toWSDateString(
              addBusinessDays(
                payable.events.estimatedDepositAt,
                1 + payoutDelay
              ),
              "monthDayYear"
            )}`
          : "Payable was paid outside of Wingspan",
        date: payable.events.paidAt
      });
    }

    if (payable.events.memberDisputedAt) {
      activity.push({
        title: `Disputed by ${payeeEmail}`,
        description: `Dispute reason: ${payable.member.comment}`,
        date: payable.events.memberDisputedAt
      });
    }

    if (payable.events.clientResolvedDisputeAt) {
      activity.push({
        title: "Dispute resolved",
        description: `You resolved dispute on ${toWSDateString(
          payable.events.clientResolvedDisputeAt
        )}`,
        date: payable.events.clientResolvedDisputeAt
      });
    }

    if (payable.events.memberAcceptedAt) {
      activity.push({
        title: "Invoice accepted by contractor",
        description: toWSDateString(payable.events.memberAcceptedAt),
        date: payable.events.memberAcceptedAt
      });
    }

    if (payable.events.clientDeclinedAt) {
      activity.push({
        title: `Rejected by ${user.email}`,
        description: `Reject reason: ${payable.client.comment}`,
        date: payable.events.clientDeclinedAt
      });
    }

    if (payable.events.memberResubmittedAt) {
      activity.push({
        title: `Resubmitted by ${payeeEmail}`,
        description: `Contractor resolved dispute at ${toWSDateString(
          payable.events.memberResubmittedAt
        )}`,
        date: payable.events.memberResubmittedAt
      });
    }

    return activity.sort((a, b) => {
      if (a.date > b.date) return -1;
      if (a.date < b.date) return 1;
      return 0;
    });
  }, [
    approverEmail,
    payable.client.comment,
    payable.createdAt,
    payable.events.approvedAt,
    payable.events.clientDeclinedAt,
    payable.events.clientResolvedDisputeAt,
    payable.events.depositedAt,
    payable.events.emailReceivedAt,
    payable.events.emailViewedAt,
    payable.events.estimatedDepositAt,
    payable.events.memberAcceptedAt,
    payable.events.memberDisputedAt,
    payable.events.memberResubmittedAt,
    payable.events.openedAt,
    payable.events.paidAt,
    payable.events.paymentInTransitAt,
    payable.events.preApprovedAt,
    payable.events.sentDue3DaysAgoAt,
    payable.events.sentDue7DaysAgoAt,
    payable.events.sentDueIn3DaysAt,
    payable.events.sentDueTodayAt,
    payable.events.unapprovedAt,
    payable.member.comment,
    payeeEmail,
    payoutDelay,
    preApproverEmail,
    unapproverEmail,
    user.email
  ]);

  return <Activity activity={activity} />;
};

const getEventActorEmail = (
  eventActorId: string | undefined,
  user: INewUser,
  authorizations?: IAuthorizationListResponse
) => {
  if (eventActorId) {
    const authorization = authorizations?.find(
      a => a.requestingUserId === eventActorId
    );

    if (authorization?.requestingUser.email) {
      return authorization.requestingUser.email;
    }
  }

  return user.email;
};
